import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ArrowRight } from "lucide-react";
import { track } from "@/lib/analytics";

export const TRAVEL_TYPES = [
  "Group Travel", "Cruises", "All-Inclusive Resorts", "Villas & Private Escapes",
  "Destination Celebrations", "Family & Friends Getaways",
  "Corporate & Group Retreats", "Romance Travel & Honeymoons",
  "Custom Travel Experiences",
];

const field = "w-full bg-transparent border-b border-white/15 py-3 font-body text-ink placeholder-white/30 focus:border-gold focus:outline-none transition-colors";
const selCls = `${field} appearance-none cursor-pointer`;
const lbl = "block font-body text-[11px] uppercase tracking-[0.15em] text-white/60 mb-1";

export const GROUP_TRAVEL_TYPES = ["Group Travel", "Corporate & Group Retreats", "Family & Friends Getaways", "Destination Celebrations"];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const YEARS = ["2026", "2027", "2028"];
const DURATIONS = ["About 3–5 nights", "About 7–10 nights", "About 10–14 nights", "2+ weeks", "Flexible"];

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const empty = {
  first_name: "", last_name: "", email: "", phone: "", travel_type: TRAVEL_TYPES[0], destination: "",
  date_type: "", departure_date: "", return_date: "", travel_window: "", travel_year: "", trip_duration: "",
  travelers: "", rooms: "", is_group_trip: "", referred_by: "", departure_city: "", purpose: "", details: "", contact_method: "Email", website: "",
};

export default function QuoteForm({ prefill }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(prefill).filter(([, v]) => v)) }));
      document.getElementById("inquiry-form")?.scrollIntoView({ behavior: "smooth" });
      toast.success("Your trip ideas were added below — just add your contact details.");
    }
  }, [prefill]);

  const validateDates = () => {
    if (form.date_type === "exact") {
      if (!form.departure_date || !form.return_date) { toast.error("Please select both your departure and return dates."); return false; }
      if (form.return_date < form.departure_date) { toast.error("Your return date can't be before your departure date."); return false; }
      return true;
    }
    if (form.date_type === "approximate") {
      if (!form.travel_window || !form.travel_year || !form.trip_duration) {
        toast.error("Please choose a rough month or window, a year, and an approximate duration."); return false;
      }
      return true;
    }
    toast.error("Please tell us your travel timing — exact or approximate dates both work.");
    return false;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) { toast.error("Please share your name and email."); return; }
    if (!validateDates()) return;
    if (!form.rooms) { toast.error("Please let us know how many rooms you'll need."); return; }
    const travel_dates = form.date_type === "exact"
      ? `${fmtDate(form.departure_date)} → ${fmtDate(form.return_date)} (exact dates)`
      : `${form.travel_window} ${form.travel_year} · ${form.trip_duration} (approximate dates)`;
    setLoading(true);
    try {
      const { data } = await api.post("/quotes", { ...form, travel_dates });
      track("quote_submit", { travel_type: form.travel_type, source_page: "start-your-journey" });
      if (GROUP_TRAVEL_TYPES.includes(form.travel_type)) track("group_submit", { travel_type: form.travel_type, source_page: "start-your-journey" });
      toast.success(data.message || "Thanks — we'll be in touch soon.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  return (
    <section id="inquiry-form" data-testid="quote-section" className="relative py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-t border-white/10">
      <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="font-body text-[11px] uppercase tracking-[0.4em] text-gold mb-6">Start Here</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light leading-[0.98] tracking-tight">Start Planning Your Trip</h2>
            <p className="font-body text-white/55 mt-7 text-sm leading-relaxed font-light max-w-sm">
              Tell us a little about the trip you're planning. One of our Travel Experience Specialists will review your
              details and help you explore the right options for your journey.
            </p>
            <p className="font-body text-white/55 mt-5 text-sm leading-relaxed font-light max-w-sm">
              This is a planning request — not a booking, and no payment is involved. After you submit, a specialist
              reviews your trip and reaches out with tailored options and next steps.
            </p>
            <p className="font-body text-white/55 mt-5 text-sm leading-relaxed font-light max-w-sm">
              Already know exactly what you want? <Link to="/ReadyToBook" className="text-gold link-underline">Send a Ready to Book request</Link> instead.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7 lg:col-start-6">
          <Reveal delay={0.15}>
            <form data-testid="quote-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
              {/* honeypot (hidden from users) */}
              <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={form.website} onChange={set("website")} className="hidden" />
              <div><label htmlFor="quote-first-name" className={lbl}>First Name</label>
                <input id="quote-first-name" data-testid="quote-first-name" autoComplete="given-name" className={field} value={form.first_name} onChange={set("first_name")} /></div>
              <div><label htmlFor="quote-last-name" className={lbl}>Last Name</label>
                <input id="quote-last-name" data-testid="quote-last-name" autoComplete="family-name" className={field} value={form.last_name} onChange={set("last_name")} /></div>
              <div><label htmlFor="quote-email" className={lbl}>Email Address</label>
                <input id="quote-email" data-testid="quote-email" type="email" inputMode="email" autoComplete="email" className={field} value={form.email} onChange={set("email")} /></div>
              <div><label htmlFor="quote-phone" className={lbl}>Phone</label>
                <input id="quote-phone" data-testid="quote-phone" type="tel" inputMode="tel" autoComplete="tel" className={field} value={form.phone} onChange={set("phone")} /></div>
              <div><label htmlFor="quote-travel-type" className={lbl}>What type of trip are you planning?</label>
                <select id="quote-travel-type" data-testid="quote-travel-type" className={selCls} value={form.travel_type} onChange={set("travel_type")}>
                  {TRAVEL_TYPES.map((s) => <option key={s} value={s} className="bg-noir text-ink">{s}</option>)}
                </select></div>
              <div><label htmlFor="quote-destination" className={lbl}>Destination or places you're considering</label>
                <input id="quote-destination" data-testid="quote-destination" className={field} value={form.destination} onChange={set("destination")} /></div>

              {/* Structured travel timing — required; exact or approximate */}
              <div className="md:col-span-2">
                <p className={lbl}>When would you like to travel? <span className="text-gold">*</span></p>
                <div className="flex flex-wrap gap-3 mt-1" role="group" aria-label="Date certainty">
                  {[["exact", "Exact dates"], ["approximate", "Approximate / flexible"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`quote-datetype-${v}`} onClick={() => setForm({ ...form, date_type: v })}
                      aria-pressed={form.date_type === v}
                      className={`px-5 py-2.5 border font-body text-sm transition-colors ${form.date_type === v ? "bg-gold text-black border-gold" : "border-white/15 text-white/75 hover:border-gold hover:text-gold"}`}>{l}</button>
                  ))}
                </div>
                {form.date_type === "exact" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-5">
                    <div><label htmlFor="quote-dep" className={lbl}>Departure Date</label>
                      <input id="quote-dep" data-testid="quote-departure" type="date" min={todayISO()} className={`${field} [color-scheme:dark]`}
                        value={form.departure_date} onChange={(e) => setForm({ ...form, departure_date: e.target.value, return_date: form.return_date && form.return_date < e.target.value ? "" : form.return_date })} /></div>
                    <div><label htmlFor="quote-ret" className={lbl}>Return Date</label>
                      <input id="quote-ret" data-testid="quote-return" type="date" min={form.departure_date || todayISO()} className={`${field} [color-scheme:dark]`}
                        value={form.return_date} onChange={set("return_date")} /></div>
                  </div>
                )}
                {form.date_type === "approximate" && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-5">
                    <div><label htmlFor="quote-window" className={lbl}>Month / window</label>
                      <select id="quote-window" data-testid="quote-window" className={selCls} value={form.travel_window} onChange={set("travel_window")}>
                        <option value="" className="bg-noir">Select…</option>
                        {MONTHS.map((m) => <option key={m} value={m} className="bg-noir">{m}</option>)}
                      </select></div>
                    <div><label htmlFor="quote-year" className={lbl}>Year</label>
                      <select id="quote-year" data-testid="quote-year" className={selCls} value={form.travel_year} onChange={set("travel_year")}>
                        <option value="" className="bg-noir">Select…</option>
                        {YEARS.map((y) => <option key={y} value={y} className="bg-noir">{y}</option>)}
                      </select></div>
                    <div><label htmlFor="quote-duration" className={lbl}>Approx. duration</label>
                      <select id="quote-duration" data-testid="quote-duration" className={selCls} value={form.trip_duration} onChange={set("trip_duration")}>
                        <option value="" className="bg-noir">Select…</option>
                        {DURATIONS.map((d) => <option key={d} value={d} className="bg-noir">{d}</option>)}
                      </select></div>
                  </div>
                )}
              </div>

              <div>
                <p className={lbl}>Is this a group trip?</p>
                <div className="flex gap-3 mt-1" role="group" aria-label="Is this a group trip?">
                  {[["yes", "Yes"], ["no", "No"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`quote-group-${v}`} onClick={() => setForm({ ...form, is_group_trip: v, travelers: v === "yes" ? form.travelers : (form.travelers === "Not sure yet" ? "" : form.travelers) })}
                      aria-pressed={form.is_group_trip === v}
                      className={`px-5 py-2.5 border font-body text-sm transition-colors ${form.is_group_trip === v ? "bg-gold text-black border-gold" : "border-white/15 text-white/75 hover:border-gold hover:text-gold"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div><label htmlFor="quote-travelers" className={lbl}>{form.is_group_trip === "yes" ? "Estimated Group Size" : "Approximate number of travelers"}</label>
                <input id="quote-travelers" data-testid="quote-travelers" inputMode="numeric" className={`${field} disabled:opacity-40`} value={form.travelers} disabled={form.travelers === "Not sure yet"} onChange={set("travelers")} />
                {form.is_group_trip === "yes" && (
                  <p className="font-body text-[11px] text-white/40 mt-2 font-light">An estimate is fine — this can change later.{" "}
                    <button type="button" data-testid="quote-group-unsure" onClick={() => setForm({ ...form, travelers: form.travelers === "Not sure yet" ? "" : "Not sure yet" })}
                      className="text-gold link-underline">{form.travelers === "Not sure yet" ? "Enter a number instead" : "Not sure yet"}</button>
                  </p>
                )}</div>
              <div><label htmlFor="quote-rooms" className={lbl}>Number of rooms needed <span className="text-gold">*</span></label>
                <select id="quote-rooms" data-testid="quote-rooms" className={selCls} value={form.rooms} onChange={set("rooms")}>
                  <option value="" className="bg-noir">Select…</option>
                  {Array.from({ length: 10 }, (_, i) => String(i + 1)).map((n) => <option key={n} value={n} className="bg-noir">{n}</option>)}
                  <option value="10+" className="bg-noir">10+</option>
                </select>
                <p className="font-body text-[11px] text-white/40 mt-2 font-light">A rough count is fine — room configurations are finalized with your specialist.</p></div>

              <div className="md:col-span-2"><label htmlFor="quote-referred-by" className={lbl}>Who referred you? <span className="text-white/25">(optional)</span></label>
                <input id="quote-referred-by" data-testid="quote-referred-by" placeholder="A friend's or past traveler's name — e.g. Berdie, Monique" className={field} value={form.referred_by} onChange={set("referred_by")} /></div>
              <div className="md:col-span-2"><label htmlFor="quote-details" className={lbl}>Tell us about your trip</label>
                <textarea id="quote-details" data-testid="quote-details" rows={4} className={`${field} resize-none`} placeholder="Who's coming, the occasion, and anything you're picturing…" value={form.details} onChange={set("details")} /></div>
              <button data-testid="quote-submit-btn" type="submit" disabled={loading}
                className="md:col-span-2 group mt-2 inline-flex items-center justify-center gap-3 border border-gold bg-gold text-black px-8 py-4 font-body text-xs uppercase tracking-[0.22em] hover:bg-transparent hover:text-gold transition-colors duration-300 disabled:opacity-50">
                {loading ? "Sending..." : "Start Your Journey"}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <p data-testid="inquiry-privacy-notice" className="md:col-span-2 font-body text-xs text-white/40 leading-relaxed font-light -mt-2">
                By submitting this form, you agree to our <Link to="/terms" className="text-gold link-underline">Terms of Use</Link> and acknowledge our <Link to="/privacy" className="text-gold link-underline">Privacy Policy</Link>. We'll use your information to respond to your travel request.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
